// @ts-strict-ignore
import * as React from 'react';
import {
    DisallowedTokensForFA,
    FeatureAffiliationType,
    Integration,
    IntegrationCapability,
    IntegrationType,
    RequireAtLeastOneOfTokensForFA,
} from '../types';
import { ModalProps } from 'antd/lib/modal';
import Modal from 'antd/lib/modal/Modal';
import { flatMap } from 'lodash';
import { allSupportedIntegrationTypesForCapability, validatePattern } from '../helpers';
import useForm from 'antd/lib/form/hooks/useForm';
import Select from 'antd/lib/select';
import Form from 'antd/lib/form/Form';
import FormItem from 'antd/lib/form/FormItem';
import Input from 'antd/lib/input';
import { useSelector } from 'react-redux';
import { RootState } from '../reducers/reducers';
import { AppComponentProps } from '../typescriptHelpers';
import { selectOrgProviders, selectIsRunwayOrg } from '../selectors/selectors';
import Typography from 'antd/lib/typography';
import Text from 'antd/lib/typography/Text';

interface NewFeatureAffiliationProps extends AppComponentProps {
    open: boolean;
    featureManagementIntegrations: Integration[];
    isLoading: boolean;
    onCancelAddNewFeatureAffiliation: ModalProps['onCancel'];
    onOkNewFeatureAffiliation: (FeatureAffiliationRequest) => void;
}

export interface FeatureAffiliationRequest {
    pattern: string;
    integrationId: IntegrationType;
    affiliationType: FeatureAffiliationType;
}

const NewFeatureAffiliationModal = (props: NewFeatureAffiliationProps) => {
    const [selectedIntegrationId, setSelectedIntegrationId] = React.useState(null);
    const [selectedAffiliationType, setSelectedAffiliationType] = React.useState(null);
    const [formIsValid, setFormIsValid] = React.useState(false);
    const [form] = useForm();
    const isRunwayOrg = useSelector((state: RootState) => selectIsRunwayOrg(state));

    const integrationProviders = useSelector((state: RootState) => selectOrgProviders(state, props.orgId));

    const reset = () => {
        setSelectedIntegrationId(null);
        setSelectedAffiliationType(null);

        form.resetFields();
    };

    const onValuesChange = () => {
        const integrationId = form.getFieldValue('integrationId');
        const affiliationType = form.getFieldValue('affiliationType');

        setSelectedIntegrationId(integrationId);
        setSelectedAffiliationType(affiliationType);
    };

    const onFieldsChange = () => {
        const allTouched = form.isFieldsTouched(true);
        const errors = flatMap(form.getFieldsError().map((field) => field.errors));

        setFormIsValid(errors.length === 0 && allTouched);
    };

    const onOk = () => {
        form.validateFields().then((values) => {
            const request = {
                pattern: values['pattern'],
                integrationId: values['integrationId'],
                affiliationType: values['affiliationType'],
            };

            props.onOkNewFeatureAffiliation(request);
        });
    };

    const onCancel = (e) => {
        props.onCancelAddNewFeatureAffiliation(e);

        reset();
    };

    const featureAffiliationOptions = (integration: Integration) => {
        if (
            allSupportedIntegrationTypesForCapability(
                IntegrationCapability.FeatureManagement,
                isRunwayOrg,
                props.orgId,
            ).includes(integration.id)
        ) {
            const provider = integrationProviders.find((provider) => provider.id === integration.providerId);

            if (provider) {
                return provider.data.featureAffiliationTypes.map((fa, index) => {
                    return (
                        <Select.Option key={index} value={fa.affiliationType}>
                            {fa.name}
                        </Select.Option>
                    );
                });
            }
        }

        return null;
    };

    return (
        <Modal
            title="New feature affiliation"
            open={props.open}
            onCancel={onCancel}
            onOk={onOk}
            okText="Create affiliation"
            confirmLoading={props.isLoading}
            okButtonProps={{ loading: props.isLoading, disabled: !formIsValid }}
            afterClose={() => {
                reset();
            }}
        >
            <Form
                form={form}
                name="newFeatureAffiliation"
                labelCol={{ span: 8 }}
                wrapperCol={{ span: 16, offset: 1 }}
                labelAlign="left"
                autoComplete="off"
                onValuesChange={onValuesChange}
                onFieldsChange={onFieldsChange}
            >
                <FormItem label="Integration name" name="integrationId" rules={[{ required: true }]}>
                    <Select placeholder="Select">
                        {props.featureManagementIntegrations.map((integration, index) => {
                            return (
                                <Select.Option key={index} value={integration.id}>
                                    {integration.name}
                                </Select.Option>
                            );
                        })}
                    </Select>
                </FormItem>
                <FormItem label="Affiliation type" name="affiliationType" rules={[{ required: true }]}>
                    <Select placeholder="Select" disabled={!selectedIntegrationId}>
                        {selectedIntegrationId &&
                            featureAffiliationOptions(
                                props.featureManagementIntegrations.find((i) => i.id === selectedIntegrationId),
                            )}
                    </Select>
                </FormItem>
                <FormItem
                    label="Affiliation pattern"
                    name="pattern"
                    rules={[
                        { required: true },
                        {
                            validator: (rule, value) => {
                                const pattern = value as string;

                                return validatePattern(
                                    pattern,
                                    'Feature affiliation',
                                    RequireAtLeastOneOfTokensForFA,
                                    DisallowedTokensForFA,
                                );
                            },
                        },
                    ]}
                >
                    <Input
                        placeholder="e.g. release-{version}"
                        disabled={!selectedAffiliationType}
                        autoComplete="off"
                    />
                </FormItem>
                <Text type="secondary">
                    Read more about tokenized patterns in our{' '}
                    <Typography.Link
                        href="https://docs.runway.team/getting-started/pattern-strings-tokens"
                        target="_blank"
                    >
                        docs.
                    </Typography.Link>
                </Text>
            </Form>
        </Modal>
    );
};

export default NewFeatureAffiliationModal;
